"use client";

// Slim top bar across the content area — v0.2.3, extended v0.2.6.
// Left: the mobile nav button, then where you are — crumbs, then the page title.
// It is the ONLY place a page is named; the pages themselves start at their
// content. Right: the persona control and Sign out.
//
// The control on the right is the USER MENU, and everyone has one — it is where
// the app keeps a person's own settings, of which the dark/light theme is the
// first. What differs by identity is what is INSIDE it: a lens holder — an
// admin, or an AM, AM Ops or Creative Specialist over their own brands —
// additionally gets the lens roster. Nobody else may look through somebody
// else's.
//
// The menu is built in two parts, and the split is the point. The theme switch
// is PINNED at the top; the lens roster below it is its own inset, scrolling
// panel. A roster runs to dozens of people, and a setting that scrolled
// away with them would be unreachable exactly when the list is longest.

import { Fragment, useEffect, useRef, useState, useTransition } from "react";
import { IntentLink } from "@/components/intent-link";
import { usePathname } from "next/navigation";
import { useAppRouter } from "@/lib/app-router";
import { ChevronDown, ChevronRight, ShieldCheck, ClipboardList, Video, Store, PenLine, UserSearch, Check, Menu, LogOut, Settings2 } from "lucide-react";
import clsx from "clsx";
import { usePersona } from "./persona-context";
import type { Persona, SwitchableLens } from "./persona-context";
import { useMobileNav } from "./mobile-nav-context";
import { usePageMeta } from "./page-meta-context";
import { personaKey, personaEquals } from "@/lib/persona-scope";
import { canOpen } from "@/lib/route-access";
import { headerFor } from "@/lib/page-headers";
import { hereOf, lensHref, withLens } from "@/lib/lens-url";
import { signOutAction } from "@/app/actions/sign-out";
import { clearAnalyticsIdentity } from "@/lib/product-analytics";
import { ThemeToggle } from "./theme-toggle";
import { TaskChip } from "./task-chip";

const P = "rgb(var(--c-accent))";

/** Your own settings' page. */
const YOUR_SETTINGS = "/settings/you";

const hasRole = (role: Persona["role"]) => (l: SwitchableLens) => l.persona.role === role;

export function PersonaTopbar() {
  const { persona, lensKey, setPersona, label, isAdmin, canSwitch, userName, switchablePersonas, brandLabel } = usePersona();
  const { toggle: toggleMobileNav } = useMobileNav();
  const router = useAppRouter();
  const pathname = usePathname();
  const header = headerFor(pathname);
  const { meta, crumbs: pageCrumbs, titleHidden } = usePageMeta();
  // The path's constant crumbs, then the ones only the page knows (a brand's pod).
  const crumbs = header ? [...header.crumbs, ...pageCrumbs] : [];
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const roleIcon = persona.role === "admin" ? <ShieldCheck size={13} style={{ color: P }} />
    : persona.role === "am" ? <ClipboardList size={13} className="text-teal-light" />
    // The same clipboard as an AM — ops reads the same work, one level up.
    : persona.role === "am_ops" ? <ClipboardList size={13} className="text-teal-light" />
    // And the same again: a Creative Specialist holds an AM's keys.
    : persona.role === "creative_specialist" ? <ClipboardList size={13} className="text-teal-light" />
    : persona.role === "brand" ? <Store size={13} className="text-amber-400" />
    // A pen, not the creator's camera: a writer's half of the job ends at the
    // document. Same green — they are working the same desk.
    : persona.role === "writer" ? <PenLine size={13} className="text-green-400" />
    // The Creator Selection glyph, not the creator's camera — this lens is the
    // page it holds. Green, because the people it is about are creators.
    : persona.role === "creator_expert" ? <UserSearch size={13} className="text-green-400" />
    : <Video size={13} className="text-green-400" />;

  // The roster comes from the server, and only for a lens holder — the lens
  // list is a list of colleagues, and nobody else gets handed one. Grouped here
  // rather than server-side because the grouping is chrome.
  //
  // A STAFF SEAT'S GROUPS ARE NOT THE ADMIN'S. An admin's roster is the whole
  // staff by role, with the console first. An AM's is herself and the people and
  // brands she works for: her own entry is the roster's first (the server puts
  // it there), so it is "You" rather than an "AM" group of one, and there is no
  // "Admin" group at all.
  const GROUPS: Array<{ group: string; holds: (l: SwitchableLens, i: number) => boolean }> = isAdmin
    ? [
        { group: "Admin", holds: hasRole("admin") },
        { group: "AM", holds: hasRole("am") },
        { group: "Creative Specialist", holds: hasRole("creative_specialist") },
        { group: "AM Ops", holds: hasRole("am_ops") },
        { group: "Creator Expert", holds: hasRole("creator_expert") },
        { group: "Creator", holds: hasRole("creator") },
        { group: "Writer", holds: hasRole("writer") },
        { group: "Brand", holds: hasRole("brand") },
      ]
    : [
        { group: "You", holds: (_l, i) => i === 0 },
        { group: "Creators", holds: (l, i) => i > 0 && l.persona.role === "creator" },
        { group: "Writers", holds: (l, i) => i > 0 && l.persona.role === "writer" },
        { group: "Brands", holds: (l, i) => i > 0 && l.persona.role === "brand" },
      ];
  const options = GROUPS.map(({ group, holds }) => ({
    group,
    items: switchablePersonas.filter(holds),
  })).filter((g) => g.items.length > 0);

  // Dropdown entries name the persona being VIEWED AS, not the signed-in user.
  // The admin entry is a capability ("see everything"), not a person — which is
  // why it is the one entry with no name in it.
  const itemLabel = (p: Persona) =>
    p.role === "admin" ? "Admin (full access)"
      // A brand lens is one brand (switchablePersonas mints one per brand), and
      // it may have no person behind it, so the brand is its label.
      : p.role === "brand" ? p.brands.map((slug) => brandLabel(slug)).join(", ")
      // Two writers for two different creators are two different lenses, and
      // their own names do not say which is which. The creator does.
      : p.role === "writer" ? `${p.displayName} → ${p.creatorName}`
      : p.displayName;

  // A NAME IS A LABEL, AND LABELS COLLIDE. Two colleagues can be called the same
  // thing — nothing in the roster forbids it, and Slack overwrites a person's
  // name on every sign-in, so it can happen without anybody choosing it. Picking
  // the wrong lens is not a small mistake here: it is reading somebody else's
  // work, and every action taken from it is scoped to them.
  //
  // So the name stays the label, and the ID appears UNDER it — but only for the
  // entries that actually clash. Printing an email against every row would cost
  // the whole menu its legibility to disambiguate a case that is usually absent.
  const clashes = new Set(
    switchablePersonas
      .map((l) => itemLabel(l.persona))
      .filter((l, i, all) => all.indexOf(l) !== i),
  );

  // ⚠️ A SWITCH IS A NAVIGATION, AND IT GOES INTO HISTORY. The lens is the
  // URL's `?as=<key>` (persona-context.tsx derives it on every render), so a
  // switch pushes the address you are on with the new key: Back returns to the
  // lens you left, as well as the page, and Forward comes back. Every other param
  // and the hash stay as they are; a page that owns URL picks drops the ones the
  // new lens cannot make itself (the Videos page).
  //
  // ⚠️ AND IT IS THE ONE PLACE A LENS MOVES YOU, the only exception to "a page
  // that is not yours is a 404". Switching persona can strand you. Admin is a
  // console, so most surfaces belong to somebody else's nav — become a creator
  // while sitting on /settings and you are on a page your sidebar cannot return
  // you to, and since the page gates became 404s (lib/server/page-gate.tsx) it
  // would answer "this doesn't exist" for a change YOU made to your own view.
  // So: keep the page if the new role has a link to it, and go to "/?as=<key>"
  // if not. "/" picks that role's landing route on the SERVER (app/page.tsx),
  // and carries the key into the address it redirects to, so the client needs
  // no copy of that rule.
  //
  // PUSH, NOT REPLACE, on that branch too. It used to replace, on the grounds
  // that the page you left is not one this role can be on. That stopped being
  // true when Back started restoring the old lens along with the old page: the
  // entry you left is valid again, under the lens it names.
  //
  // ⚠️ THE CHROME AND THE DATA COMMIT TOGETHER, and there is nothing left here
  // to make them. A switch changes what two places read — the lens in React
  // context (the sidebar, this button, every client page) and the lens the
  // server renders through (`currentLens()`) — and until this change the first
  // was React state and the second a cookie, so a refresh had to be asked for
  // and wrapped in one transition with the state update, or the app drew an
  // AM's chrome around the admin's server-rendered data for 50–500 ms (#178,
  // #186). Now both read the URL: the context derives the lens from the
  // address the router commits, and the server renders from the same address
  // (proxy.ts → activeLensFor), so they arrive in one commit by construction.
  // The transition stays only for `aria-busy`, below: "has this switch landed?"
  // still needs an answer in the DOM.
  //
  // ⚠️ THE COOKIE IS STILL WRITTEN IMMEDIATELY (setPersona), because it is what
  // an address with no `?as=` is rendered through — a new tab, a typed URL. What
  // it no longer decides is a reload: a switch interrupted half way (a reload, a
  // fetch that fails) leaves the address you were on, which names the OLD lens,
  // and the URL wins (L4). That is the right way round now — the address bar is
  // the statement of what is on screen — and the cookie is put back to match it
  // as soon as that page shows (components/lens-url-sync.tsx).
  //
  // ⚠️ AND WHATEVER GOES WRONG HERE NEXT, THE REPAIR IS NOT A LONGER WAIT.
  // persona-gating.spec.ts has gone red on four PRs across three days,
  // including one whose whole diff was a shell script and two markdown files,
  // and every assertion it makes retries for its full ten seconds — so a red
  // there has never once meant an impatient test. It means the switch did not
  // land. A wait that gets longer buys a green suite over a live bug, which is
  // the one outcome worse than red.
  const [switching, startSwitch] = useTransition();
  const picked = useRef<SwitchableLens | null>(null);
  const [pickTick, setPickTick] = useState(0);
  const handledTick = useRef(0);
  useEffect(() => {
    // ⚠️ ASKED FOR AFTER THE COMMIT THAT CLOSES THE MENU, NEVER FROM THE CLICK.
    // The menu — including the very button clicked — lives inside `{open && …}`
    // below, so `setOpen(false)` unmounts the initiator, and a navigation started
    // in that same commit went missing about one switch in twenty (#178). Kept
    // for the push exactly as it was for the refresh.
    //
    // ONCE PER PICK, AND ONLY PER PICK. `pathname` is a dependency because the
    // effect reads it — and it CHANGES underneath, on the goes-home branch, the
    // moment the redirect lands. Without this guard that re-entry is read as a
    // second pick and switches again.
    if (pickTick === handledTick.current) return; // mount, or a plain re-render
    handledTick.current = pickTick;
    const lens = picked.current;
    if (!lens) return;
    // The lens already on: the address already names it, so there is nothing to
    // navigate to. The cookie is rewritten all the same — it may have been moved
    // by another tab, and this is the lens the person just said they want.
    if (lens.key === lensKey) {
      setPersona(lens);
      return;
    }
    const stays = canOpen(lens.persona.role, pathname);
    const here = stays ? hereOf(window.location) : { pathname: "/", params: [], hash: "" };
    startSwitch(() => {
      setPersona(lens);
      router.push(withLens(here, lens.key), { scroll: !stays });
    });
  }, [pickTick, pathname, router, setPersona, lensKey]);

  // The menu closes at once — it is chrome, not data, and a dropdown left
  // standing under a switcher that has not moved yet reads as a click that did
  // nothing. Everything else waits for the effect above, and therefore for the
  // commit that closed this menu.
  const pick = (lens: SwitchableLens) => {
    picked.current = lens;
    setOpen(false);
    setPickTick((n) => n + 1);
  };

  return (
    // data-testid so a page can MEASURE it. The Videos page scrolls a renamed
    // week to just under this bar, and the page it scrolls in is the window —
    // there is no inner scroll container (see app/layout.tsx) — so content
    // passes UNDERNEATH this, and anything aiming at "the top of the viewable
    // area" has to subtract however tall this is. Measured rather than assumed:
    // `h-12` is a fact about this file, and a page hardcoding 48 would be
    // silently wrong the day it stops being true.
    <div
      data-testid="persona-topbar"
      className="sticky top-0 z-40 h-12 flex items-center justify-between px-3 md:px-6 bg-chrome border-b border-hairline"
    >
      {/* Where you are. The agency names itself once, in the sidebar. */}
      <div className="flex items-center gap-2 min-w-0">
        <button
          onClick={toggleMobileNav}
          aria-label="Open navigation"
          className="md:hidden shrink-0 -ml-1 p-1.5 text-text-muted hover:text-fg transition-colors"
        >
          <Menu size={18} />
        </button>

        {!titleHidden && header && (header.title ?? meta) && (
          <>
            {/* Crumbs collapse below sm: on a phone the title alone has to fit
                beside the persona control, and the way back is the OS's. */}
            {crumbs.length > 0 && (
              <nav aria-label="Breadcrumb" className="hidden sm:flex items-center gap-1.5 shrink-0">
                {crumbs.map((crumb, i) => (
                  <Fragment key={`${i}:${crumb.href}`}>
                    <IntentLink
                      // The lens rides on a crumb as it does on the sidebar's
                      // links, so following one is one render (lib/lens-url.ts).
                      // Prefetched on hover or focus, as the sidebar's.
                      href={lensHref(crumb.href, lensKey)}
                      data-testid="crumb"
                      className="text-[12px] text-text-dim hover:text-fg transition-colors whitespace-nowrap"
                    >
                      {crumb.label}
                    </IntentLink>
                    <ChevronRight size={11} className="text-text-dim shrink-0" />
                  </Fragment>
                ))}
              </nav>
            )}

            {/* Still the page's <h1> — it just lives in the chrome now, so every
                page has exactly one and screen readers still announce it. A null
                static title (the editor) falls back to the dynamic one; there is
                never both a title AND a trailing meta fragment. */}
            <h1 data-testid="page-title" className="text-[13px] font-semibold text-fg truncate">
              {header.title ?? meta}
            </h1>
          </>
        )}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {/* The Operations Inbox's chip: today's count, or the card being worked
            and the way back to the inbox. Only for a lens that can open it. */}
        <TaskChip />
        {/* ONE TRIGGER, TWO IDENTITIES. Both open the same menu; they differ
            only in what the button says and what the menu contains. The testids
            stay split — `persona-switcher` for a lens holder, `persona-static`
            for everyone else — because that distinction is exactly what the
            gating specs assert, and it is still true: only one of these can
            change the lens. Gated on canSwitch, from the SIGNED-IN identity,
            never on persona.role, which is "creator" while an AM views as Jay
            and would hide the roster she needs to come back. */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid={canSwitch ? "persona-switcher" : "persona-static"}
            aria-haspopup="menu"
            aria-expanded={open}
            // ⚠️ THE ONE THING ON SCREEN THAT SAYS A SWITCH IS STILL IN FLIGHT.
            // The label cannot say it: the label is the lens you are LOOKING AT,
            // and it deliberately does not move until the server render that
            // matches it has arrived. So without this the button between the
            // click and that arrival is indistinguishable from a click that did
            // nothing — which is the shape of bug this whole file is about.
            //
            // Still clickable, on purpose. A switch that never lands must leave
            // the menu reachable; a disabled control would trap the one person
            // who could pick their way back out of it.
            aria-busy={switching}
            className={clsx(
              "flex items-center gap-2 px-3 py-1.5 rounded-lg text-[12px] font-semibold text-fg bg-surface border border-hairline hover:border-line transition-colors",
              switching && "opacity-60 cursor-progress",
            )}
          >
            {roleIcon}
            {/* Who you are → what you are viewing. Without the identity half, an
                admin viewing as Acsah is indistinguishable from Acsah herself,
                and Acsah viewing as Jay from Jay. Only a lens holder can be
                viewing as anyone else, so only a lens holder's button carries
                it. */}
            {canSwitch && <span className="text-text-muted font-normal hidden sm:inline">{userName} →</span>}
            {/* And the same disambiguation on the button, for the same reason it
                is in the menu: "Creator: Jay Crotty" says nothing about WHICH Jay
                you are currently reading as, and everything done from this lens
                is scoped to that person. Only when it clashes — the label is in
                the chrome of every page, and this is not a place to spend a line
                of email on a case that is usually absent. */}
            {clashes.has(itemLabel(persona)) ? `${label} (${personaKey(persona)})` : label}
            <ChevronDown size={12} className={clsx("text-text-dim transition-transform", open && "rotate-180")} />
          </button>

          {open && (
            <div
              data-testid="user-menu"
              // A flex COLUMN with the cap on the outside: the header keeps its
              // natural height, and the roster below is the only thing that
              // scrolls. Capping each part separately would let a long roster
              // push the switch off the bottom of the screen.
              className="absolute right-0 top-full mt-1.5 w-60 flex flex-col max-h-[70vh] bg-surface border border-line rounded-lg shadow-menu overflow-hidden"
            >
              {/* ── Pinned: who you are, and your own settings ────────────── */}
              <div className="shrink-0">
                <div className="px-3 pt-2.5 pb-2">
                  <div className="text-[9px] font-mono uppercase tracking-widest font-semibold text-text-dim">Signed in as</div>
                  <div className="text-[12px] font-semibold text-fg truncate">{userName}</div>
                </div>
                <ThemeToggle />
                {/* Your own settings (the scope your Operations Inbox opens on): whoever the page is open to. */}
                {canOpen(persona.role, YOUR_SETTINGS) && (
                  <IntentLink
                    href={lensHref(YOUR_SETTINGS, lensKey)}
                    onClick={() => setOpen(false)}
                    data-testid="your-settings-link"
                    className="flex w-full items-center gap-2 px-3 py-2.5 text-[12px] font-medium text-text transition-colors hover:bg-card"
                  >
                    <Settings2 size={13} className="text-accent" />
                    Your settings
                  </IntentLink>
                )}
              </div>

              {/* ── The lens roster: a lens holder's tool, and it looks like one ─
                  Inset on the page colour with its own heading and a hard top
                  edge, so it reads as a separate instrument bolted under the
                  menu rather than more items in the same list. Changing what you
                  are LOOKING AT is a different kind of act from changing how the
                  app is PAINTED, and the two should not sit in one undivided
                  column of rows. */}
              {canSwitch && options.length > 0 && (
                <div
                  data-testid="persona-menu"
                  className="flex min-h-0 flex-col border-t border-line bg-inset"
                >
                  <div className="shrink-0 flex items-center gap-1.5 px-3 pt-2.5 pb-1.5">
                    <ShieldCheck size={11} className="text-accent shrink-0" />
                    <span className="text-[9px] font-mono uppercase tracking-widest font-semibold text-text-dim">Viewing as</span>
                  </div>

                  {/* min-h-0 is what makes this scroll at all: a flex child
                      defaults to min-height:auto, which refuses to shrink below
                      its content, so the box would grow past the menu's cap and
                      overflow instead of scrolling. */}
                  <div className="min-h-0 overflow-y-auto">
                    {options.map(({ group, items }) => (
                      <div key={group} className="border-b border-hairline last:border-0">
                        <div className="px-3 pt-2 pb-1 text-[9px] font-mono uppercase tracking-widest font-semibold text-text-dim">{group}</div>
                        {items.map((lens, i) => {
                          const p = lens.persona;
                          const active = personaEquals(persona, p);
                          const label = itemLabel(p);
                          return (
                            <button
                              key={`${group}-${i}`}
                              onClick={() => pick(lens)}
                              // The id, never the label: unique by construction, so a
                              // caller that must name one exactly — a test, say — has
                              // something to name it by that two people cannot share.
                              data-testid={`persona-option-${personaKey(p)}`}
                              className={clsx(
                                "w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-[12px] transition-colors",
                                active ? "text-fg" : "text-text-muted hover:text-fg hover:bg-card"
                              )}
                              style={active ? { background: "rgb(var(--c-accent) / 0.0941)" } : {}}
                            >
                              <span className="flex min-w-0 flex-col">
                                <span className="truncate">{label}</span>
                                {clashes.has(label) && (
                                  <span className="truncate text-[10px] text-text-dim">{personaKey(p)}</span>
                                )}
                              </span>
                              {active && <Check size={12} className="shrink-0" style={{ color: P }} />}
                            </button>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <form action={signOutAction} onSubmit={clearAnalyticsIdentity}>
          <button
            type="submit"
            data-testid="sign-out"
            title={`Signed in as ${userName}`}
            aria-label="Sign out"
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold text-text-dim border border-hairline hover:text-fg hover:border-line transition-colors"
          >
            <LogOut size={13} />
            <span className="hidden sm:inline">Sign out</span>
          </button>
        </form>
      </div>
    </div>
  );
}
